/**
 * Fail-closed gate for the experimental Excel native ReAct executor.
 *
 * The ConfigCat client belongs to the PowerPoint workspace, which Excel cannot
 * import across the repository boundary. The service publishes the resolved
 * boolean here, matching the existing Excel model-selection bridge.
 */

/** Executor architecture captured once when an Excel run starts. */
export type ExcelExecutorMode = "plan-protocol" | "native-react";

let excelNativeReactEnabled = false;
let excelNativeReactFlagReader: (() => boolean) | null = null;

/**
 * Register the live feature-service reader.
 *
 * @param reader - Current ConfigCat-backed reader, or null during disposal.
 * @returns Nothing.
 */
export function registerExcelNativeReactFlagReader(
  reader: (() => boolean) | null,
): void {
  excelNativeReactFlagReader = reader;
}

/**
 * Publish a successfully resolved ConfigCat value.
 *
 * @param enabled - Whether new Excel runs may use the native executor.
 * @returns Nothing.
 */
export function setExcelNativeReactFlag(enabled: boolean): void {
  excelNativeReactEnabled = enabled === true;
}

/**
 * Reset the experimental executor to its production-safe default.
 *
 * @returns Nothing.
 */
export function resetExcelNativeReactFlag(): void {
  excelNativeReactEnabled = false;
}

/**
 * Invalidate an unresolved flag without accidentally enabling native mode.
 *
 * @returns Nothing.
 */
export function invalidateExcelNativeReactFlag(): void {
  excelNativeReactEnabled = false;
}

/**
 * Read the current native-executor gate.
 *
 * A missing, unresolved, or throwing reader always returns false.
 *
 * @returns True only after explicit successful enablement.
 */
export function isExcelNativeReactEnabled(): boolean {
  if (excelNativeReactFlagReader) {
    try {
      return excelNativeReactFlagReader() === true;
    } catch {
      return false;
    }
  }
  return excelNativeReactEnabled === true;
}

/**
 * Capture the executor architecture for one run.
 *
 * Callers retain the returned primitive for the complete run so a mid-flight
 * remote flag refresh cannot switch providers or orchestration semantics.
 *
 * @returns The current production or experimental executor mode.
 */
export function snapshotExcelExecutorMode(): ExcelExecutorMode {
  return isExcelNativeReactEnabled() ? "native-react" : "plan-protocol";
}
